import React, { useEffect, useState, memo, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogActions, DialogContent, DialogTitle
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { PaymentTerminalViewModel } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { PaymentProviderAutoComplete, PaymentProviderConfigAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const PaymentTerminalEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { id = null } = params || {};
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(PaymentTerminalViewModel & { hasChanges?: boolean; originalValues?: Partial<PaymentTerminalViewModel>; }) | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      authService
        .get<PaymentTerminalViewModel>(`/settingsApi/GetPaymentTerminal?id=${id}`)
        .then((response) => setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null))
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultModel: PaymentTerminalViewModel & { originalValues: Partial<PaymentTerminalViewModel>; hasChanges: boolean } = {
        createNew: true,
        originalValues: {},
        hasChanges: false,
      } as PaymentTerminalViewModel & { originalValues: Partial<PaymentTerminalViewModel>; hasChanges: boolean };
      defaultModel.originalValues = { ...defaultModel };
      setViewModel(defaultModel);
    }
  }, [id, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return; // Prevent save if validation fails
    var newItem = viewModel.createNew ?? false;

    setIsLoading(true);
    try {
      const response = await authService.post<PaymentTerminalViewModel>("/settingsApi/CreateOrUpdatePaymentTerminal", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/paymentterminal/edit", params: { id: response.id } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof PaymentTerminalViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as PaymentTerminalViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };


  const handleDelete = async () => {

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/paymentterminal/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeletePaymentTerminal`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");;

      navigate({ path: "/settings/paymentterminal/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };





  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (!viewModel.description || viewModel.description.trim() === "") {
      showSnackbar(t("help.descriptionRequired"), "warning");
      return false;
    }


    return true;
  };
  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.paymentTerminal")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </Box>
        {viewModel && (
          <>
            <Grid container spacing={2}>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <TextField
                    label={t("common.description")}
                    value={viewModel.description || ""}
                    onChange={(e) => handleChange("description", e.target.value)}
                    fullWidth
                    margin="normal"
                    error={!viewModel.description || viewModel.description.trim() === ""}
                    helperText={!viewModel.description || viewModel.description.trim() === "" ? t("help.descriptionRequired") : ""}
                  />
                </FormControl>
              </Grid>


              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <PaymentProviderAutoComplete
                    key={viewModel.paymentProvider || null}
                    valueId={viewModel.paymentProvider || null}
                    onChange={(selected) => handleChange("paymentProvider", selected?.value || "")}
                    infoText={t("help.paymentProvider")}
                    error={!viewModel.paymentProvider || viewModel.paymentProvider.trim() === ""}
                    helperText={
                      !viewModel.paymentProvider?.trim()
                        ? t("help.paymentProviderRequired")
                        : ""
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <PaymentProviderConfigAutoComplete
                    key={viewModel.paymentProviderId || null}
                    valueId={viewModel.paymentProviderId || null}
                    onChange={(selected) => handleChange("paymentProviderId", selected?.value || "")}
                    infoText={t("help.paymentProviderSetting")}
                    error={!viewModel.paymentProviderId || viewModel.paymentProviderId.trim() === ""}
                    helperText={
                      !viewModel.paymentProviderId?.trim()
                        ? t("help.paymentProviderSettingRequired")
                        : ""
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <TextField
                    label={t("common.cardTerminalId")}
                    value={viewModel.cardTerminalInfo1 || ""}
                    onChange={(e) => handleChange("cardTerminalInfo1", e.target.value)}
                    fullWidth
                    margin="normal"
                    error={!viewModel.cardTerminalInfo1 || viewModel.cardTerminalInfo1.trim() === ""}
                    helperText={
                      !viewModel.cardTerminalInfo1?.trim()
                        ? t("help.cardTerminalIdRequired")
                        : ""
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>

    </>
  );
};

export default PaymentTerminalEditPage;
